import { useRef, useState } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useEffect } from 'react';
import { apiMatch } from '../../api/apiMatch.api';
import { ProfileExampleByData } from '../../components/profile/ProfileExampleByData';
import { MeetActionButtons } from './MeetActionButtons';
import { toast } from 'react-toastify';
import DotSpinner from '../../components/shared/DotSpinner';
import { useFilters } from './hooks/useFilters';
import { LimitModal } from '../../components/limits/LimitModal';
import { checkLimitError } from '../../components/limits/utils/checkLimitError';
import { NotificationBySubscription } from '../subscription/NotificationBySubscription';
import { RouletteMatchScreen } from '../roulette/RouletteMatchScreen';
import { apiUser } from '../../api/apiUser.api';
import GeolocationRequest from '../../components/GeolocationRequest';
import config from '../../config';
import { useSubscription } from '../../hooks/useSubscription';
import { getActiveFiltersCount, saveFiltersToLocalStorage } from '../meet/utils/filters';
import { Button } from '../../components/Button';
import { useNavigate } from 'react-router-dom';
import { getAdController } from '../../utils/adsgram';

const SUB_NOTIFY_DAILY_KEY = 'sub_notify_last_forced_date';

const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const minCount = 40;
const openChatNotifyMinCount = 50;
const countToShowADS = 20;

export function Meet() {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { isActiveSubscription } = useSubscription();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showGeolocationRequest, setShowGeolocationRequest] = useState(false);

  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [openChatNotifyCount, setOpenChatNotifyCount] = useState(0);

  const [userData, setUserData] = useState(null);

  const [userMatches, setUsersMatches] = useState([]);
  const [lastUserMatches, setLastUserMatches] = useState(null);
  const [limitsKey, setLimitsKey] = useState(null);
  const [showMatchesScreen, setShowMatchesScreen] = useState(false);

  const currentViewerUser = userMatches?.[0] ?? null;
  const isCurrentUserLikeMe = currentViewerUser?.likedMe;
  const [isLikeLastUserForMe, setIsLikeLastUserForMe] = useState(false);

  const { filters } = useFilters();
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const loadingRef = useRef(null);

  useEffect(() => {
    if (!filters) return;

    const fetch = async () => {
      setIsLoading(true);
      await fetchPotentialUsers(1, true);
      await fetchLimitCheckUser();
      setIsLoading(false);
    };
    fetch();
  }, [filters]);

  useEffect(() => {
    if (userMatches.length <= 1) {
      fetchPotentialUsers(page + 1);
    }
  }, [userMatches]);

  useEffect(() => {
    apiUser.fetchMe(axiosPrivate).then((res) => {
      setUserData(res.data);
    });
  }, []);

  useEffect(() => {
    if (isActiveSubscription !== false) return;

    const today = getTodayKey();
    const lastForcedDate = localStorage.getItem(SUB_NOTIFY_DAILY_KEY);

    if (lastForcedDate !== today) {
      localStorage.setItem(SUB_NOTIFY_DAILY_KEY, today);
      setSubscriptionCount(minCount);
    }
  }, [isActiveSubscription]);

  const checkCoordinates = () => {
    const coordinate = userData?.location?.coordinates || [];
    if (coordinate.length < 2 || coordinate.every((c) => c === 0)) {
      setShowGeolocationRequest(true);
    }
  };

  const fetchPotentialUsers = async (nextPage = 1, replace = false) => {
    if (!filters || (!replace && nextPage > totalPage) || loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await apiMatch.fetchPotential(axiosPrivate, { filters, page: nextPage });

      setUsersMatches((prev) => (replace ? response.users : [...prev, ...response.users]));
      setTotalPage(response.totalPages);
      setPage(nextPage);
    } catch (error) {
      if (error.response.data.message) {
        toast.error(error.response.data.message);
      }
    }

    loadingRef.current = false;
  };

  const fetchLimitCheckUser = async () => {
    try {
      await apiMatch.fetchLimitCheck(axiosPrivate);
    } catch (error) {
      if (error.response.data.message) {
        toast.error(error.response.data.message);
      }
    }
  };

  const deleteFirstUser = () => {
    setLastUserMatches(currentViewerUser);
    setUsersMatches((prev) => {
      return prev.filter((_, i) => i !== 0);
    });
  };

  const handleLikeOrDisLikeUser = async ({ targetUserId, like = true }) => {
    if (!targetUserId) return;

    setIsActionLoading(true);

    try {
      await apiMatch.fetchLikeOrDisLike(axiosPrivate, {
        targetUserId,
        like
      });

      deleteFirstUser();

      if (like && isCurrentUserLikeMe) {
        setShowMatchesScreen(true);
        setLastUserMatches((prev) => ({
          ...prev,
          likedMe: false
        }));
      }

      setIsLikeLastUserForMe(like);
    } catch (error) {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      const limitKey = checkLimitError(message);

      if (status === 429 && limitKey) {
        deleteFirstUser();
        setLimitsKey(limitKey);
      }

      if (status === 409 && code === 'ALREADY_LIKED') {
        deleteFirstUser();
      }
      if (!limitKey) {
        toast.error(message);
      }
    } finally {
      setIsActionLoading(false);
      checkCoordinates();
    }
  };

  const handleSuperLikeUser = async ({ targetUserId }) => {
    if (!targetUserId) return;

    setIsActionLoading(true);

    try {
      await apiMatch.fetchSuperLike(axiosPrivate, {
        targetUserId
      });

      if (isCurrentUserLikeMe) {
        setShowMatchesScreen(true);
        setLastUserMatches((prev) => ({
          ...prev,
          likedMe: false
        }));
      }

      setIsLikeLastUserForMe(true);
      deleteFirstUser();
    } catch (error) {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      const limitKey = checkLimitError(message);
      console.log('limitKey', limitKey);

      if (status === 429 && limitKey) {
        setLimitsKey(limitKey);
      }

      if (status === 409 && code === 'ALREADY_LIKED') {
        deleteFirstUser();
      }

      if (!limitKey) {
        toast.error(message);
      }
    } finally {
      setIsActionLoading(false);
      checkCoordinates();
    }
  };

  const handleMatchReturn = async () => {
    if (!lastUserMatches) return;
    setIsActionLoading(true);

    try {
      await apiMatch.fetchMatchReturn(axiosPrivate, { targetUserId: lastUserMatches._id });
      setUsersMatches((prev) => [lastUserMatches, ...prev]);
      setLastUserMatches(null);
      setIsLikeLastUserForMe(false);
    } catch (error) {
      const message = error?.response?.data?.message;

      const limitKey = checkLimitError(message);

      if (limitKey) {
        setLimitsKey(limitKey);
      } else {
        toast.error(message);
      }
    }

    setIsActionLoading(false);
  };

  const incrementSwipeCounters = () => {
    setSubscriptionCount((prev) => prev + 1);
    setOpenChatNotifyCount((prev) => prev + 1);
  };

  const showAd = async () => {
    try {
      const controller = getAdController();
      const result = await controller.show();
      console.log('Реклама просмотрена:', result);
    } catch (error) {
      // error.description может содержать 'too long session'
      if (error?.description?.includes('too long') || error?.state === 'tooLongSession') {
        // Показываем пользователю сообщение и перезагружаем Mini App
        toast.warning('Сессия слишком долгая. Перезапускаем приложение...', {
          onClose: () => {
            window.location.reload(); // или tg.close(); tg.open();
          }
        });
      } else {
        console.error('Ошибка показа рекламы:', error);
      }
    }
  };

  const decrementsADSCounter = () => {
    const count = localStorage.getItem('adsgramCount') || 0;
    const newCount = parseInt(count) + 1;
    localStorage.setItem('adsgramCount', newCount);

    if (newCount >= countToShowADS) {
      showAd();
      localStorage.setItem('adsgramCount', 0);
    }
  };

  const handleGeolocationComplete = async (coordinates) => {
    try {
      await axiosPrivate.put(config.API.USERS.UPDATE_LOCATION, {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        accuracy: coordinates.accuracy
      });
      await apiUser.fetchMe(axiosPrivate).then((res) => {
        setUserData(res.data);
      });
    } catch (error) {
      console.error('Ошибка сохранения геолокации:', error);
      toast.error('Ошибка сохранения геолокации');
    }
    setShowGeolocationRequest(false);
  };

  const isNoMoreFavorite = !currentViewerUser && !isLoading;

  return (
    <div
      className={`relative h-[calc(100%-178px)] rounded-2xl mx-4  ${
        subscriptionCount >= minCount ? 'overflow-hidden' : 'overflow-y-auto'
      }`}
    >
      {showGeolocationRequest && (
        <GeolocationRequest onComplete={handleGeolocationComplete} onSkip={() => setShowGeolocationRequest(false)} />
      )}
      {showMatchesScreen && lastUserMatches && (
        <RouletteMatchScreen
          recipientId={lastUserMatches._id}
          userAPhotoUrl={userData?.photos[0]}
          userBPhotoUrl={lastUserMatches.photos[0]}
          close={() => setShowMatchesScreen(false)}
        />
      )}
      {!isActiveSubscription && (
        <NotificationBySubscription
          count={subscriptionCount}
          resetCount={() => setSubscriptionCount(0)}
          minCount={minCount}
        />
      )}
      {isLoading && (
        <div className="flex items-center justify-center min-h-[100vh]">
          <DotSpinner />
        </div>
      )}
      {currentViewerUser && (
        <div className="flex flex-col h-full">
          <ProfileExampleByData
            key={currentViewerUser._id}
            className="px-0 py-0 select-none h-[90%] overflow-y-auto"
            userData={currentViewerUser}
            showActionButtons={false}
          />
          {!isNoMoreFavorite && !isLoading && currentViewerUser && (
            <MeetActionButtons
              likedMe={lastUserMatches?.likedMe && !isLikeLastUserForMe}
              backCardDisabled={!lastUserMatches}
              openChatNotifyCount={openChatNotifyCount}
              openChatNotifyMinCount={openChatNotifyMinCount}
              resetOpenChatNotifyCount={() => setOpenChatNotifyCount(0)}
              backCard={() => handleMatchReturn()}
              skipCard={() => {
                handleLikeOrDisLikeUser({ targetUserId: currentViewerUser._id, like: false });
                incrementSwipeCounters();
                decrementsADSCounter();
              }}
              likeCard={() => {
                handleLikeOrDisLikeUser({ targetUserId: currentViewerUser._id, like: true });
                incrementSwipeCounters();
                decrementsADSCounter();
              }}
              superLikeCard={() => {
                handleSuperLikeUser({ targetUserId: currentViewerUser._id });
                incrementSwipeCounters();
                decrementsADSCounter();
              }}
              goToChatCard={() => currentViewerUser?._id}
              disabledButton={isActionLoading}
            />
          )}
        </div>
      )}

      {isNoMoreFavorite && !isLoading && getActiveFiltersCount() === 0 && (
        <div className="flex flex-col items-center justify-center h-full bg-[#043939] px-4">
          <img src="/icons/meet-not-found.svg" alt="meet-not-found" className="w-[100px]" />
          <p className="text-center font-semibold text-[18px] text-white mt-3">
            На сегодня анкеты закончились... <br /> Отдохните немного и приходите завтра!
          </p>
        </div>
      )}
      {isNoMoreFavorite && !isLoading && getActiveFiltersCount() > 0 && (
        <div className="flex flex-col items-center justify-center h-full bg-[#043939] px-4">
          <img src="/icons/meet-oops.svg" alt="meet-oops" className="w-[100px]" />
          <p className="text-center font-semibold text-[18px] text-white mt-3">
            Ой, по этому запросу пока ничего не нашлось
          </p>
          <Button onClick={() => navigate('/filters')} className="w-max mt-6">
            Сбросить фильтр
          </Button>
        </div>
      )}

      {limitsKey && <LimitModal domain="swipe" limitsKey={limitsKey} setLimitsKey={setLimitsKey} />}
    </div>
  );
}
